import { useState } from 'react'
import {
    Box,
    Stack,
    Button,
    IconButton,
    Popover,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material'
import { CalendarMonth } from '@mui/icons-material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay'
import dayjs, { Dayjs } from 'dayjs'

export interface DateRange {
    from: Dayjs | null
    to: Dayjs | null
}

// Calendar day that paints the selected from–to span (range highlight isn't
// built into the free DateCalendar, so we style days ourselves).
const RangeDay: React.FC<
    PickersDayProps & { rangeFrom?: Dayjs | null; rangeTo?: Dayjs | null }
> = ({ rangeFrom, rangeTo, ...props }) => {
    const { day } = props
    const isStart = rangeFrom ? day.isSame(rangeFrom, 'day') : false
    const isEnd = rangeTo ? day.isSame(rangeTo, 'day') : false
    const inRange =
        rangeFrom && rangeTo
            ? day.isAfter(rangeFrom, 'day') && day.isBefore(rangeTo, 'day')
            : false
    return (
        <PickersDay
            {...props}
            selected={isStart || isEnd}
            sx={{
                ...(inRange && {
                    backgroundColor: 'rgba(139,196,65,0.18)',
                    borderRadius: 0,
                }),
                ...((isStart || isEnd) && {
                    backgroundColor: '#8BC441 !important',
                    color: '#fff',
                    fontWeight: 700,
                }),
            }}
        />
    )
}

interface Props {
    /** Preset day spans rendered as toggle buttons. */
    presets?: number[]
    /** Selected preset span (ignored visually while a custom range is active). */
    rangeDays: number
    onRangeDays: (days: number) => void
    /** Free-form custom range. When both ends are set it overrides the preset. */
    customRange: DateRange
    onCustomRange: (range: DateRange) => void
    size?: 'small' | 'medium'
}

// Preset day-span toggles + a free-form calendar range, extracted from the
// fleet-insights header so any view can drop in the same control. The calendar
// uses a flight-booking-style pick: first click sets From, second sets To.
const UniversalTimeRange = ({
    presets = [7, 14, 30, 90],
    rangeDays,
    onRangeDays,
    customRange,
    onCustomRange,
    size = 'small',
}: Props) => {
    const [calAnchor, setCalAnchor] = useState<HTMLElement | null>(null)
    const [draftRange, setDraftRange] = useState<DateRange>({
        from: null,
        to: null,
    })
    const customActive = Boolean(customRange.from && customRange.to)

    const pickDay = (d: Dayjs | null) => {
        if (!d) return
        setDraftRange((r) => {
            if (!r.from || (r.from && r.to)) return { from: d, to: null }
            if (d.isBefore(r.from, 'day')) return { from: d, to: null }
            return { from: r.from, to: d }
        })
    }

    const applyCustomRange = (range: DateRange) => {
        if (!range.from || !range.to) return
        onCustomRange(range)
        setCalAnchor(null)
    }

    return (
        <Stack direction="row" alignItems="center" spacing={1.5}>
            <ToggleButtonGroup
                size={size}
                exclusive
                value={customActive ? null : rangeDays}
                onChange={(_e, v) => {
                    if (v) {
                        onRangeDays(v)
                        onCustomRange({ from: null, to: null })
                    }
                }}
                sx={{
                    '& .MuiToggleButton-root': {
                        px: 1.5,
                        py: 0.4,
                        color: 'text.secondary',
                        borderColor: 'divider',
                    },
                    '& .Mui-selected': {
                        color: 'primary.main',
                    },
                }}
            >
                {presets.map((d) => (
                    <ToggleButton key={d} value={d}>
                        {d}d
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>

            {/* Custom date range */}
            <IconButton
                size={size}
                onClick={(e) => {
                    setDraftRange(customRange)
                    setCalAnchor(e.currentTarget)
                }}
                sx={{
                    color: customActive ? 'primary.main' : 'text.secondary',
                    border: '1px solid',
                    borderColor: customActive ? 'primary.main' : 'divider',
                    borderRadius: 1,
                    p: 0.5,
                }}
                title="Custom date range"
            >
                <CalendarMonth fontSize="small" />
            </IconButton>
            <Popover
                open={Boolean(calAnchor)}
                anchorEl={calAnchor}
                onClose={() => setCalAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Box sx={{ p: 1.5, width: 320 }}>
                        {/* Editable From / To — type a date directly, or use the
                            calendar below. Enter applies. */}
                        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                            <DatePicker
                                label="From"
                                value={draftRange.from}
                                maxDate={draftRange.to || dayjs()}
                                format="MM/DD/YY"
                                onChange={(d) =>
                                    setDraftRange((r) => ({ ...r, from: d }))
                                }
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        fullWidth: true,
                                        onKeyDown: (e: any) => {
                                            if (e.key === 'Enter')
                                                applyCustomRange(draftRange)
                                        },
                                    },
                                }}
                            />
                            <DatePicker
                                label="To"
                                value={draftRange.to}
                                minDate={draftRange.from || undefined}
                                maxDate={dayjs()}
                                format="MM/DD/YY"
                                onChange={(d) =>
                                    setDraftRange((r) => ({ ...r, to: d }))
                                }
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        fullWidth: true,
                                        onKeyDown: (e: any) => {
                                            if (e.key === 'Enter')
                                                applyCustomRange(draftRange)
                                        },
                                    },
                                }}
                            />
                        </Stack>

                        <DateCalendar
                            value={draftRange.to || draftRange.from}
                            maxDate={dayjs()}
                            onChange={pickDay}
                            slots={{ day: RangeDay as any }}
                            slotProps={
                                {
                                    day: {
                                        rangeFrom: draftRange.from,
                                        rangeTo: draftRange.to,
                                    } as any,
                                } as any
                            }
                            sx={{ width: '100%', m: 0 }}
                        />

                        <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="flex-end"
                            sx={{ px: 0.5, pb: 0.5 }}
                        >
                            <Button
                                size="small"
                                color="inherit"
                                onClick={() => {
                                    setDraftRange({ from: null, to: null })
                                    onCustomRange({ from: null, to: null })
                                    setCalAnchor(null)
                                }}
                            >
                                Clear
                            </Button>
                            <Button
                                size="small"
                                variant="contained"
                                disabled={!draftRange.from || !draftRange.to}
                                onClick={() => applyCustomRange(draftRange)}
                            >
                                Apply
                            </Button>
                        </Stack>
                    </Box>
                </LocalizationProvider>
            </Popover>
        </Stack>
    )
}

export default UniversalTimeRange
